import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  waiting_for_info: 'bg-orange-100 text-orange-800',
  label_sent: 'bg-cyan-100 text-cyan-800',
  in_transit: 'bg-indigo-100 text-indigo-800',
  received: 'bg-teal-100 text-teal-800',
  offer_sent: 'bg-purple-100 text-purple-800',
  offer_confirmed: 'bg-purple-100 text-purple-800',
  offer_revised: 'bg-purple-200 text-purple-900',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  paid: 'bg-emerald-200 text-emerald-900',
  completed: 'bg-gray-100 text-gray-800',
  archived: 'bg-gray-100 text-gray-600'
};

const STATUS_LABELS = {
  new: 'Neu',
  contacted: 'Kontaktiert',
  waiting_for_info: 'Warte auf Info',
  label_sent: 'Versandlabel verschickt',
  in_transit: 'Auf dem Weg',
  received: 'Eingegangen',
  offer_sent: 'Angebot gesendet',
  offer_confirmed: 'Angebot bestätigt',
  offer_revised: 'Angebot angepasst',
  accepted: 'Akzeptiert',
  rejected: 'Abgelehnt',
  paid: 'Ausgezahlt',
  completed: 'Abgeschlossen',
  archived: 'Archiviert'
};

const TYPE_COLORS = {
  b2b: 'bg-[#143e6e] text-white',
  c2b: 'bg-[#1f7db6] text-white',
  c2b_laptop: 'bg-blue-500 text-white',
  c2b_smartphone: 'bg-green-500 text-white'
};

export const AdminLeads = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    lead_type: '',
    status: '',
    priority: ''
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [labelFile, setLabelFile] = useState(null);
  const [labelNote, setLabelNote] = useState('');
  const [labelSending, setLabelSending] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerReason, setOfferReason] = useState('');
  const [offerSending, setOfferSending] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/devices');
      return;
    }
    fetchStats();
    fetchLeads();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      fetchLeads();
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/leads/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.lead_type) params.append('lead_type', filters.lead_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await fetch(`${BACKEND_URL}/api/admin/leads?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setLeads(data.leads);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Fehler beim Laden der Leads');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Status aktualisiert');
        fetchLeads();
        fetchStats();
        // refresh modal lead if it's open
        if (selectedLead && selectedLead.id === leadId) {
          refreshSelectedLead(leadId);
        }
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const refreshSelectedLead = async (leadId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setSelectedLead(data.lead);
    } catch (e) { /* noop */ }
  };

  const addNote = async (leadId, text) => {
    if (!text.trim()) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/notes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: text.trim() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Notiz hinzugefügt');
        refreshSelectedLead(leadId);
      } else {
        toast.error('Notiz konnte nicht gespeichert werden');
      }
    } catch (e) {
      toast.error('Fehler beim Speichern der Notiz');
    }
  };

  const deleteNote = async (leadId, noteId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Notiz gelöscht');
        refreshSelectedLead(leadId);
      }
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Lead wirklich unwiderruflich löschen? Alle Fotos werden ebenfalls entfernt.')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Lead gelöscht');
        setShowDetailModal(false);
        setSelectedLead(null);
        fetchLeads();
        fetchStats();
      } else {
        toast.error('Löschen fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  const sendShippingLabel = async () => {
    if (!selectedLead) return;
    setLabelSending(true);
    try {
      const token = localStorage.getItem('adminToken');
      const fd = new FormData();
      if (labelFile) fd.append('file', labelFile);
      if (labelNote) fd.append('note', labelNote);
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${selectedLead.id}/send-shipping-label`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Versandanleitung gesendet');
        setShowLabelModal(false);
        setLabelFile(null);
        setLabelNote('');
        refreshSelectedLead(selectedLead.id);
        fetchLeads();
      } else {
        toast.error(data.detail || 'E-Mail-Versand fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Senden');
    } finally {
      setLabelSending(false);
    }
  };

  const sendFinalOffer = async () => {
    if (!selectedLead) return;
    const priceNum = parseFloat(offerPrice);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Bitte gültigen Preis eingeben');
      return;
    }
    setOfferSending(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${selectedLead.id}/send-final-offer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ final_price: priceNum, reason: offerReason || null })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Finales Angebot gesendet (Status: ${data.status})`);
        setShowOfferModal(false);
        setOfferReason('');
        refreshSelectedLead(selectedLead.id);
        fetchLeads();
      } else {
        toast.error(data.detail || 'E-Mail-Versand fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Senden');
    } finally {
      setOfferSending(false);
    }
  };

  const openLabelModal = () => {
    setLabelFile(null);
    setLabelNote('');
    setShowLabelModal(true);
  };

  const openOfferModal = () => {
    setOfferPrice(selectedLead?.finalPrice?.toString() || selectedLead?.calculatedPrice?.toString() || '');
    setOfferReason('');
    setShowOfferModal(true);
  };

  const openMailto = (subject, body) => {
    if (!selectedLead?.email) return;
    const url = `mailto:${selectedLead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  };

  const exportCSV = async (type = null) => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = type ? `?lead_type=${type}` : '';
      const response = await fetch(`${BACKEND_URL}/api/admin/leads/export/csv${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${type || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Export erfolgreich');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLeadTypeBadge = (lead) => {
    if (lead.type === 'b2b') return { label: 'B2B', color: TYPE_COLORS.b2b };
    if (lead.deviceType === 'laptop' || lead.category === 'laptop') 
      return { label: 'Privat Laptop', color: TYPE_COLORS.c2b_laptop };
    if (lead.deviceType === 'smartphone' || lead.category === 'smartphone')
      return { label: 'Privat Smartphone', color: TYPE_COLORS.c2b_smartphone };
    return { label: 'Privat', color: TYPE_COLORS.c2b };
  };

  const KPICard = ({ title, value, icon: Icon, color, change }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{title}</p>
              <h3 className="text-3xl font-bold text-[#143e6e]">{value}</h3>
            </div>
            <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
              <Icon className="text-white" size={24} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#143e6e]">Lead Management</h1>
              <p className="text-gray-600 mt-1">Alle Anfragen verwalten</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/admin/devices')} variant="outline">
                <Icons.Settings className="mr-2" size={18} />
                Geräte verwalten
              </Button>
              <Button onClick={() => exportCSV()} className="bg-[#1f7db6] hover:bg-[#143e6e]">
                <Icons.Download className="mr-2" size={18} />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            <KPICard title="Gesamt" value={stats.totalLeads} icon={Icons.Users} color="bg-[#143e6e]" />
            <KPICard title="Neu" value={stats.newLeads} icon={Icons.Bell} color="bg-blue-500" />
            <KPICard title="B2B" value={stats.b2bLeads} icon={Icons.Building2} color="bg-purple-500" />
            <KPICard title="Privat" value={stats.privateLeads} icon={Icons.User} color="bg-green-500" />
            <KPICard title="High Value" value={stats.highValueLeads} icon={Icons.TrendingUp} color="bg-orange-500" />
            <KPICard title="Angebote" value={stats.pendingOffers} icon={Icons.Clock} color="bg-yellow-500" />
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Input
                  placeholder="Suchen... (Name, Email, Firma)"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full"
                />
              </div>
              <Select value={filters.lead_type} onValueChange={(v) => setFilters({ ...filters, lead_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Lead Typ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Alle Typen</SelectItem>
                  <SelectItem value="b2b">B2B</SelectItem>
                  <SelectItem value="private">Privat</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Alle Status</SelectItem>
                  <SelectItem value="new">Neu</SelectItem>
                  <SelectItem value="contacted">Kontaktiert</SelectItem>
                  <SelectItem value="offer_sent">Angebot gesendet</SelectItem>
                  <SelectItem value="accepted">Akzeptiert</SelectItem>
                  <SelectItem value="completed">Abgeschlossen</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchLeads} className="bg-[#1f7db6] hover:bg-[#143e6e]">
                <Icons.RefreshCw className="mr-2" size={18} />
                Aktualisieren
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name / Firma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kontakt</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gerät</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wert</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        <Icons.Loader2 className="animate-spin mx-auto mb-2" size={32} />
                        Lädt...
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        Keine Leads gefunden
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => {
                      const typeBadge = getLeadTypeBadge(lead);
                      return (
                        <tr key={lead.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {formatDate(lead.createdAt)}
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={typeBadge.color}>{typeBadge.label}</Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.companyName || lead.name}
                            </div>
                            {lead.contactPerson && (
                              <div className="text-xs text-gray-500">{lead.contactPerson}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div>{lead.email}</div>
                            <div className="text-xs">{lead.phone}</div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="font-medium">{lead.deviceManufacturer || lead.manufacturer}</div>
                            <div className="text-xs text-gray-500">{lead.deviceModel || lead.models}</div>
                          </td>
                          <td className="px-6 py-4">
                            <Select 
                              value={lead.status || 'new'} 
                              onValueChange={(v) => updateStatus(lead.id, v)}
                            >
                              <SelectTrigger className="w-44">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-green-600">
                            {lead.calculatedPrice ? `${lead.calculatedPrice.toFixed(2)} €` : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`lead-view-${lead.id}`}
                              onClick={async () => {
                                setSelectedLead(lead);
                                setNoteInput('');
                                setShowDetailModal(true);
                                // load full detail (notes, activityLog)
                                refreshSelectedLead(lead.id);
                              }}
                            >
                              <Icons.Eye size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetailModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#143e6e]">Lead Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-gray-700">
                <Icons.X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Name / Firma</h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedLead.companyName || selectedLead.name}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Typ</h3>
                  <Badge className={getLeadTypeBadge(selectedLead).color}>
                    {getLeadTypeBadge(selectedLead).label}
                  </Badge>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Email</h3>
                  <p className="text-gray-900">{selectedLead.email}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Telefon</h3>
                  <p className="text-gray-900">{selectedLead.phone}</p>
                </div>
                {selectedLead.deviceManufacturer && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Hersteller</h3>
                      <p className="text-gray-900">{selectedLead.deviceManufacturer}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Modell</h3>
                      <p className="text-gray-900">{selectedLead.deviceModel}</p>
                    </div>
                  </>
                )}
                {selectedLead.calculatedPrice && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Berechneter Preis</h3>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedLead.calculatedPrice.toFixed(2)} €
                    </p>
                  </div>
                )}
                {selectedLead.deviceCount && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Anzahl Geräte</h3>
                    <p className="text-gray-900">{selectedLead.deviceCount}</p>
                  </div>
                )}
              </div>
              {selectedLead.additionalInfo && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Zusätzliche Informationen</h3>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded">{selectedLead.additionalInfo}</p>
                </div>
              )}

              {/* Photos Gallery */}
              {selectedLead.photoUrls && selectedLead.photoUrls.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <Icons.Image size={16} /> Hochgeladene Fotos ({selectedLead.photoUrls.length})
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedLead.photoUrls.map((url, i) => (
                      <a
                        key={i}
                        href={`${BACKEND_URL}${url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-[#1f7db6] transition group"
                        data-testid={`lead-photo-${i}`}
                      >
                        <img
                          src={`${BACKEND_URL}${url}`}
                          alt={`Foto ${i + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Internal Notes */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                  <Icons.StickyNote size={16} /> Interne Notizen
                </h3>
                <div className="space-y-2 mb-3">
                  {(selectedLead.notes && selectedLead.notes.length > 0) ? (
                    selectedLead.notes.map((n, idx) => (
                      <div key={n.id || idx} className="bg-yellow-50 border border-yellow-200 rounded p-3 flex items-start justify-between gap-3" data-testid={`note-item-${idx}`}>
                        <div className="flex-1">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.text}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(n.timestamp)} · {n.author || 'admin'}</p>
                        </div>
                        {n.id && (
                          <button
                            onClick={() => deleteNote(selectedLead.id, n.id)}
                            className="text-gray-400 hover:text-red-600 transition"
                            aria-label="Notiz löschen"
                            data-testid={`note-delete-${idx}`}
                          >
                            <Icons.Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 italic">Noch keine Notizen.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder="Neue Notiz hinzufügen..."
                    data-testid="note-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && noteInput.trim()) {
                        addNote(selectedLead.id, noteInput);
                        setNoteInput('');
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      addNote(selectedLead.id, noteInput);
                      setNoteInput('');
                    }}
                    disabled={!noteInput.trim()}
                    className="bg-[#1f7db6] hover:bg-[#143e6e]"
                    data-testid="note-add-btn"
                  >
                    <Icons.Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Activity log (compact) */}
              {selectedLead.activityLog && selectedLead.activityLog.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <Icons.History size={16} /> Verlauf
                  </h3>
                  <ul className="space-y-1 text-xs text-gray-600">
                    {selectedLead.activityLog.slice(-5).reverse().map((a, i) => (
                      <li key={i} className="flex justify-between gap-2 border-l-2 border-gray-200 pl-2">
                        <span>{a.action === 'status_changed' ? `Status → ${a.status}` : a.action}</span>
                        <span className="text-gray-400">{formatDate(a.timestamp)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button
                  onClick={openLabelModal}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="open-label-modal-btn"
                >
                  <Icons.Package className="mr-2" size={16} />
                  Versandanleitung senden
                </Button>
                <Button
                  onClick={openOfferModal}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="open-offer-modal-btn"
                >
                  <Icons.Mail className="mr-2" size={16} />
                  Finales Angebot senden
                </Button>
                <Button
                  onClick={() => deleteLead(selectedLead.id)}
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-lead-btn"
                >
                  <Icons.Trash2 className="mr-2" size={16} />
                  Lead löschen
                </Button>
                <Button onClick={() => setShowDetailModal(false)} variant="outline" className="ml-auto">
                  Schließen
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Shipping Label Modal */}
      {showLabelModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowLabelModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#143e6e] flex items-center gap-2">
                <Icons.Package size={22} /> Versandanleitung senden
              </h2>
              <button onClick={() => setShowLabelModal(false)} className="text-gray-500 hover:text-gray-700">
                <Icons.X size={22} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                <strong>Empfänger:</strong> {selectedLead.name} &lt;{selectedLead.email}&gt;<br/>
                <strong>Gerät:</strong> {selectedLead.deviceManufacturer} {selectedLead.deviceModel}<br/>
                <strong>Sofortangebot:</strong> {selectedLead.calculatedPrice?.toFixed(2)} €
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DHL-Label (PDF, optional)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setLabelFile(e.target.files?.[0] || null)}
                  data-testid="label-file-input"
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-[#1f7db6] file:text-white hover:file:bg-[#143e6e]"
                />
                {labelFile && (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                    <Icons.Check size={14} /> {labelFile.name} ({Math.round(labelFile.size / 1024)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Persönliche Nachricht (optional)</label>
                <textarea
                  value={labelNote}
                  onChange={(e) => setLabelNote(e.target.value)}
                  rows={3}
                  data-testid="label-note-input"
                  placeholder="z. B. zusätzliche Hinweise zur Verpackung..."
                  className="w-full border rounded p-2 text-sm"
                />
              </div>

              <details className="bg-gray-50 rounded p-3 text-sm">
                <summary className="cursor-pointer font-medium text-gray-700">Mail-Vorschau</summary>
                <div className="mt-2 text-xs text-gray-600 space-y-1 leading-relaxed">
                  <p><strong>Betreff:</strong> Ihr DHL-Versandlabel für den Ankauf Ihres {selectedLead.deviceManufacturer} {selectedLead.deviceModel}</p>
                  <p>Hallo {selectedLead.name},</p>
                  <p>vielen Dank für Ihre Anfrage. Anbei finden Sie Ihr kostenloses DHL-Versandlabel. Sofortangebot: <strong>{selectedLead.calculatedPrice?.toFixed(2)} €</strong> (vorbehaltlich Prüfung).</p>
                  <p>Versandadresse: wirkaufendeinlaptop.de, Goosmoortwiete 4, 25474 Bönningstedt</p>
                  {labelNote && <p>Persönliche Nachricht: {labelNote}</p>}
                </div>
              </details>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    const subject = `Ihr DHL-Versandlabel für den Ankauf Ihres ${selectedLead.deviceManufacturer} ${selectedLead.deviceModel}`;
                    const body = `Hallo ${selectedLead.name},\n\nvielen Dank für Ihre Anfrage. Bitte schicken Sie Ihr Gerät an:\n\nwirkaufendeinlaptop.de\nGoosmoortwiete 4\n25474 Bönningstedt\n\nIhr Sofortangebot: ${selectedLead.calculatedPrice?.toFixed(2)} € (vorbehaltlich Prüfung).\n\n${labelNote ? '\n' + labelNote + '\n' : ''}\nViele Grüße\nIhr Team von wirkaufendeinlaptop.de`;
                    openMailto(subject, body);
                  }}
                  variant="outline"
                  data-testid="label-mailto-btn"
                >
                  <Icons.ExternalLink className="mr-2" size={16} />
                  Im Mailprogramm öffnen
                </Button>
                <Button
                  onClick={sendShippingLabel}
                  disabled={labelSending}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="label-send-btn"
                >
                  {labelSending ? (
                    <><Icons.Loader2 className="mr-2 animate-spin" size={16}/> Wird gesendet...</>
                  ) : (
                    <><Icons.Send className="mr-2" size={16}/> Direkt senden</>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Final Offer Modal */}
      {showOfferModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowOfferModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#143e6e] flex items-center gap-2">
                <Icons.Mail size={22} /> Finales Angebot senden
              </h2>
              <button onClick={() => setShowOfferModal(false)} className="text-gray-500 hover:text-gray-700">
                <Icons.X size={22} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
                <strong>Empfänger:</strong> {selectedLead.name} &lt;{selectedLead.email}&gt;<br/>
                <strong>Gerät:</strong> {selectedLead.deviceManufacturer} {selectedLead.deviceModel}<br/>
                <strong>Ursprüngliches Sofortangebot:</strong> {selectedLead.calculatedPrice?.toFixed(2)} €
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Finaler Angebotspreis (EUR) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                  data-testid="offer-price-input"
                  className="text-2xl font-bold h-14"
                />
                {offerPrice && selectedLead.calculatedPrice && (
                  <p className={`text-xs mt-1 ${parseFloat(offerPrice) >= selectedLead.calculatedPrice ? 'text-emerald-700' : 'text-red-700'}`}>
                    {parseFloat(offerPrice) >= selectedLead.calculatedPrice ? '↑' : '↓'} {(parseFloat(offerPrice) - selectedLead.calculatedPrice).toFixed(2)} € gegenüber Sofortangebot
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Begründung (optional, empfohlen bei Preisänderung)
                </label>
                <textarea
                  value={offerReason}
                  onChange={(e) => setOfferReason(e.target.value)}
                  rows={3}
                  data-testid="offer-reason-input"
                  placeholder="z. B. „Gerät in besserem Zustand als angegeben – Bonus inklusive."
                  className="w-full border rounded p-2 text-sm"
                />
              </div>

              <details className="bg-gray-50 rounded p-3 text-sm">
                <summary className="cursor-pointer font-medium text-gray-700">Mail-Vorschau</summary>
                <div className="mt-2 text-xs text-gray-600 space-y-1 leading-relaxed">
                  <p><strong>Betreff:</strong> Ihr finales Ankaufsangebot für {selectedLead.deviceManufacturer} {selectedLead.deviceModel}: {offerPrice} €</p>
                  <p>Hallo {selectedLead.name},</p>
                  <p>wir haben Ihr Gerät erhalten und sorgfältig geprüft.</p>
                  <p className="text-2xl font-bold text-[#1f7db6]">Finales Angebot: {offerPrice} €</p>
                  {offerReason && <p>Begründung: {offerReason}</p>}
                  <p>Der Kunde kann das Angebot direkt in der Mail per Klick <strong>annehmen</strong> oder <strong>ablehnen</strong> (tokenisierte Links).</p>
                </div>
              </details>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    const subject = `Ihr finales Ankaufsangebot für ${selectedLead.deviceManufacturer} ${selectedLead.deviceModel}: ${offerPrice} €`;
                    const body = `Hallo ${selectedLead.name},\n\nwir haben Ihr Gerät erhalten und geprüft. Hier unser finales Angebot:\n\n${offerPrice} €\n\n${offerReason ? 'Begründung: ' + offerReason + '\n\n' : ''}Bitte antworten Sie auf diese E-Mail, ob Sie das Angebot annehmen.\n\nViele Grüße\nIhr Team von wirkaufendeinlaptop.de`;
                    openMailto(subject, body);
                  }}
                  variant="outline"
                  data-testid="offer-mailto-btn"
                >
                  <Icons.ExternalLink className="mr-2" size={16} />
                  Im Mailprogramm öffnen
                </Button>
                <Button
                  onClick={sendFinalOffer}
                  disabled={offerSending || !offerPrice}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="offer-send-btn"
                >
                  {offerSending ? (
                    <><Icons.Loader2 className="mr-2 animate-spin" size={16}/> Wird gesendet...</>
                  ) : (
                    <><Icons.Send className="mr-2" size={16}/> Direkt senden</>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};